import type {
  AhaGuideline,
  CmIndexTermNode,
  CmSearchSource,
  CmTabularNotes,
  IcdGuideline,
} from "../types/encoder";
import { sanitizeGuidelineText } from "./encoder";

export type CmReferenceKeys = {
  code: string;
  category: string;
};

export type CmDeepSearchHit = {
  id: string;
  title: string;
  nemod: string;
  code: string;
  path: string[];
};

export type ExpandMode = "all" | "none" | "depth";

export type ExpandState = {
  mode: ExpandMode;
  overrides: Record<string, boolean>;
  defaultDepth: number;
};

export type NoteTone = "hard" | "soft" | "prompt" | "plain";

export const CM_SOURCE_OPTIONS: Array<{ value: CmSearchSource; label: string }> =
  [
    { value: "index", label: "Diseases" },
    { value: "eindex", label: "External Cause" },
    { value: "drugs", label: "Drugs" },
    { value: "neoplasm", label: "Neoplasms" },
  ];

export const CM_DRUG_HEADERS = [
  "Substance",
  "Accidental",
  "Intentional",
  "Assault",
  "Undetermined",
  "Adverse Effect",
  "Underdosing",
] as const;

export const CM_NEOPLASM_HEADERS = [
  "Neoplasm",
  "Malignant Primary",
  "Malignant Secondary",
  "Ca in situ",
  "Benign",
  "Uncertain",
  "Unspecified",
] as const;

export const CM_NOTE_SECTIONS: Array<{
  key: keyof CmTabularNotes;
  title: string;
  tone: NoteTone;
}> = [
  { key: "includes", title: "Includes", tone: "plain" },
  { key: "inclusionTerm", title: "Inclusion terms", tone: "plain" },
  { key: "excludes1", title: "Excludes1", tone: "hard" },
  { key: "excludes2", title: "Excludes2", tone: "soft" },
  { key: "codeFirst", title: "Code first", tone: "hard" },
  { key: "useAdditionalCode", title: "Use additional code", tone: "prompt" },
  { key: "codeAlso", title: "Code also", tone: "prompt" },
  { key: "notes", title: "Notes", tone: "plain" },
];

export const CM_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const MAX_DEEP_SEARCH_HITS = 25;

export const isCmTableSource = (source: CmSearchSource): boolean =>
  source === "drugs" || source === "neoplasm";

export const isCmIndexSource = (source: CmSearchSource): boolean =>
  source === "index" || source === "eindex";

export const formatCmCode = (code: string): string => {
  const trimmed = code.trim();
  if (!trimmed) return trimmed;
  if (/[^A-Za-z0-9]/.test(trimmed)) return trimmed;
  return trimmed.length > 3
    ? `${trimmed.slice(0, 3)}.${trimmed.slice(3)}`
    : trimmed;
};

export const displayIncompleteCmCode = (
  code: string,
  incomplete: boolean
): string => {
  const formatted = formatCmCode(code);
  if (!incomplete || formatted.endsWith("-")) return formatted;
  return `${formatted}-`;
};

export const stripIncompleteSuffix = (code: string): string =>
  code.trim().replace(/-$/, "").replace(/\.$/, "");

export const toDotlessCmCode = (code: string): string =>
  stripIncompleteSuffix(code).replace(/\./g, "").toUpperCase();

export const applySeventhCharacter = (
  pureCode: string,
  char: string
): string => {
  let base = pureCode.replace(/\./g, "");
  while (base.length < 6) base += "X";
  return formatCmCode(`${base}${char}`);
};

const INDEX_LEFTOVER_STOPWORDS: Record<string, true> = {
  disease: true,
  diseases: true,
  syndrome: true,
  disorder: true,
  condition: true,
  nos: true,
  nec: true,
  unspecified: true,
  other: true,
};

export const uniqueWords = (words: string[]): string[] => {
  const seen: Record<string, true> = {};
  const out: string[] = [];
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i].trim().toLowerCase();
    if (!word || seen[word]) continue;
    seen[word] = true;
    out.push(word);
  }
  return out;
};

export const tokenizeQuery = (query: string): string[] =>
  uniqueWords(
    query
      .trim()
      .toLowerCase()
      .replace(/,/g, " ")
      .split(/\s+/)
  );

export const textMatchesSearchWord = (text: string, word: string): boolean => {
  const needle = word.toLowerCase().trim();
  if (!needle) return false;
  const tokens = text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return tokens.some((token) => token === needle || token.startsWith(needle));
};

export const leftoverIndexWords = (
  title: string,
  nemod: string,
  queryWords: string[]
): string[] => {
  const covered = `${title} ${nemod}`;
  return uniqueWords(queryWords).filter(
    (word) =>
      !INDEX_LEFTOVER_STOPWORDS[word] && !textMatchesSearchWord(covered, word)
  );
};

export const isStrongLeadMatch = (title: string, query: string): boolean => {
  const words = tokenizeQuery(query);
  if (words.length === 0) return false;
  const titleText = title.toLowerCase().trim();
  return words.some(
    (word) =>
      titleText === word ||
      titleText.startsWith(`${word} `) ||
      titleText.startsWith(`${word},`) ||
      titleText.startsWith(word)
  );
};

export const scoreCmLeadHit = (
  hit: {
    title: string;
    nemod: string;
    code: string;
    hasChildren: boolean;
    see: string;
    seeAlso: string;
    score: number;
  },
  queryWords: string[]
): number => {
  const words = uniqueWords(queryWords);
  const titleText = hit.title.toLowerCase().trim();
  let score = hit.score || 0;
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    if (titleText === word) score += 800;
    else if (
      titleText.startsWith(`${word} `) ||
      titleText.startsWith(`${word},`)
    ) {
      score += 400;
    } else if (textMatchesSearchWord(titleText, word)) {
      score += 80;
    }
  }
  const leftover = leftoverIndexWords(hit.title, hit.nemod, words);
  const extra = `${hit.nemod} ${hit.see} ${hit.seeAlso}`;
  let covered = 0;
  for (let i = 0; i < leftover.length; i += 1) {
    if (textMatchesSearchWord(extra, leftover[i])) covered += 1;
  }
  score += covered * 150;
  if (leftover.length > 0 && covered === leftover.length) score += 200;
  if (hit.hasChildren) score += 50;
  if (words.length > 1 && leftover.indexOf(words[0]) >= 0) {
    score += 180;
  }
  return score;
};

export const treeContainsSearchWord = (
  node: {
    title: string;
    nemod?: string;
    see?: string;
    seeAlso?: string;
    children: Array<{
      title: string;
      nemod?: string;
      see?: string;
      seeAlso?: string;
      children: unknown[];
    }>;
  },
  word: string
): boolean => {
  const blob = [node.title, node.nemod || "", node.see || "", node.seeAlso || ""].join(
    " "
  );
  if (textMatchesSearchWord(blob, word)) return true;
  for (let i = 0; i < node.children.length; i += 1) {
    if (
      treeContainsSearchWord(
        node.children[i] as typeof node,
        word
      )
    ) {
      return true;
    }
  }
  return false;
};

export const highlightCmMatches = (
  text: string,
  words: string[]
): Array<{ text: string; match: boolean }> => {
  const needles = uniqueWords(words).sort((a, b) => b.length - a.length);
  if (!text || needles.length === 0) {
    return text ? [{ text, match: false }] : [];
  }

  const chunks = text.match(/[a-z0-9]+|[^a-z0-9]+/gi) ?? [text];
  const parts: Array<{ text: string; match: boolean }> = [];

  for (let i = 0; i < chunks.length; i += 1) {
    const chunk = chunks[i];
    if (!/^[a-z0-9]+$/i.test(chunk)) {
      parts.push({ text: chunk, match: false });
      continue;
    }
    const lower = chunk.toLowerCase();
    let needle = "";
    for (let n = 0; n < needles.length; n += 1) {
      if (lower === needles[n] || lower.startsWith(needles[n])) {
        needle = needles[n];
        break;
      }
    }
    if (!needle) {
      parts.push({ text: chunk, match: false });
      continue;
    }
    parts.push({ text: chunk.slice(0, needle.length), match: true });
    if (chunk.length > needle.length) {
      parts.push({ text: chunk.slice(needle.length), match: false });
    }
  }

  return parts;
};

export const cleanRedirectTerm = (
  redirectTerm: string
): { cleanTerm: string; source: CmSearchSource } => {
  const cleanTerm = redirectTerm
    .replace(/^see\s/i, "")
    .replace(/^see also\s/i, "")
    .replace(/,\s*by site/gi, "")
    .replace(/,\s*specified NEC/gi, "")
    .replace(/\./g, "")
    .trim();
  const lower = cleanTerm.toLowerCase();
  if (lower.startsWith("neoplasm")) return { cleanTerm, source: "neoplasm" };
  if (lower.includes("drugs") || lower.includes("table of drugs")) {
    return { cleanTerm, source: "drugs" };
  }
  if (lower.startsWith("external cause")) {
    return { cleanTerm, source: "eindex" };
  }
  return { cleanTerm, source: "index" };
};

export const isNodeOpen = (
  state: ExpandState,
  id: string,
  depth: number
): boolean => {
  const override = state.overrides[id];
  if (override !== undefined) return override;
  if (state.mode === "all") return true;
  if (state.mode === "none") return false;
  return depth <= state.defaultDepth;
};

export const sectionLetterOf = (title: string): string | null => {
  const scrubbed = title.trim().replace(/^[^a-z0-9]+/i, "");
  const letter = scrubbed.charAt(0).toUpperCase();
  return letter >= "A" && letter <= "Z" ? letter : null;
};

export const notesHaveContent = (notes: CmTabularNotes): boolean =>
  CM_NOTE_SECTIONS.some((section) => notes[section.key].length > 0);

const suggestionCode = (node: CmIndexTermNode): string =>
  node.code || node.manif || node.subcat;

const ownText = (node: CmIndexTermNode): string =>
  `${node.title} ${node.nemod}`.toLowerCase();

export const codedSuggestionScore = (
  hit: Pick<CmDeepSearchHit, "title" | "nemod" | "path">,
  words: string[]
): number => {
  if (words.length === 0) return 0;
  const title = `${hit.title} ${hit.nemod}`;
  const path = hit.path.join(" ");
  const inTitle = words.filter((word) =>
    textMatchesSearchWord(title, word)
  ).length;
  const inPath = words.filter((word) =>
    textMatchesSearchWord(path, word)
  ).length;
  const allInTitle = inTitle === words.length;
  return (allInTitle ? 10000 : 0) + inTitle * 1000 + inPath * 10;
};

export const collectDeepSearchHits = (
  root: CmIndexTermNode,
  words: string[]
): CmDeepSearchHit[] => {
  if (words.length === 0) return [];
  const hits: Array<CmDeepSearchHit & { score: number; depth: number }> = [];

  const walk = (
    node: CmIndexTermNode,
    path: string[],
    pathText: string,
    depth: number
  ) => {
    const self = ownText(node);
    const code = suggestionCode(node);
    if (code) {
      const combined = `${pathText} ${self}`;
      if (words.every((word) => textMatchesSearchWord(combined, word))) {
        const hit: CmDeepSearchHit = {
          id: node.id,
          title: node.title,
          nemod: node.nemod,
          code,
          path,
        };
        hits.push({
          ...hit,
          depth,
          score: codedSuggestionScore(hit, words),
        });
      }
    }
    const childPath = path.concat([node.title]);
    const childPathText = `${pathText} ${self}`;
    for (let i = 0; i < node.children.length; i += 1) {
      walk(node.children[i], childPath, childPathText, depth + 1);
    }
  };

  walk(root, [], "", 0);
  return hits
    .sort((a, b) => b.score - a.score || a.depth - b.depth)
    .slice(0, MAX_DEEP_SEARCH_HITS)
    .map((hit) => ({
      id: hit.id,
      title: hit.title,
      nemod: hit.nemod,
      code: hit.code,
      path: hit.path,
    }));
};

export const codedSuggestionLine = (hit: CmDeepSearchHit): string => {
  const title = hit.nemod ? `${hit.title} ${hit.nemod}` : hit.title;
  return hit.path.length > 0 ? `${hit.path.join(" > ")} > ${title}` : title;
};

export const buildCmReferenceKeys = (
  code: string | null
): CmReferenceKeys | null => {
  if (!code) return null;
  const clean = code.trim().toUpperCase().replace(/-+$/, "");
  if (clean.length < 3) return null;
  return { code: clean, category: clean.slice(0, 3) };
};

const uniqueById = <T extends { id: number }>(items: T[]): T[] => {
  const seen: Record<number, true> = {};
  const unique: T[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (seen[item.id]) continue;
    seen[item.id] = true;
    unique.push(item);
  }
  return unique;
};

const rankByCodeMention = <T extends { id: number; fyear: number | null }>(
  items: T[],
  toText: (item: T) => string,
  keys: CmReferenceKeys
): T[] => {
  const unique = uniqueById(items);
  const byYearDesc = (a: T, b: T) =>
    (b.fyear ?? 0) - (a.fyear ?? 0) || a.id - b.id;
  const exact: T[] = [];
  const related: T[] = [];
  for (let i = 0; i < unique.length; i += 1) {
    const item = unique[i];
    if (toText(item).toUpperCase().includes(keys.code)) exact.push(item);
    else related.push(item);
  }
  return exact.sort(byYearDesc).concat(related.sort(byYearDesc));
};

export const rankCmGuidelines = (
  results: IcdGuideline[],
  keys: CmReferenceKeys
): IcdGuideline[] =>
  rankByCodeMention(
    results,
    (guideline) =>
      [
        guideline.keywords,
        guideline.codingNote,
        guideline.chapter,
        guideline.subChapter,
        guideline.topic,
        guideline.subTopic,
      ]
        .filter(Boolean)
        .join(" "),
    keys
  );

export const rankAhaResultsByCmCode = (
  results: AhaGuideline[],
  keys: CmReferenceKeys
): AhaGuideline[] =>
  rankByCodeMention(
    results,
    (result) =>
      [
        result.keywords,
        result.codingClinicOverview,
        result.codingClinicQa,
        result.informationStatement,
      ]
        .filter(Boolean)
        .join(" "),
    keys
  );

export const cmGuidelineTitle = (guideline: IcdGuideline): string => {
  const title =
    guideline.topic?.trim() ||
    guideline.subTopic?.trim() ||
    guideline.subChapter?.trim() ||
    guideline.chapter?.trim() ||
    "ICD-10-CM Official Guideline";
  return sanitizeGuidelineText(title);
};

export const cmGuidelineTrail = (guideline: IcdGuideline): string => {
  const title = cmGuidelineTitle(guideline);
  return [guideline.chapter, guideline.subChapter, guideline.subTopic]
    .map((part) => sanitizeGuidelineText(part?.trim() ?? ""))
    .filter((part) => Boolean(part) && part !== title)
    .join(" › ");
};

export const filterLineageTree = <
  T extends {
    title: string;
    nemod?: string;
    see?: string;
    seeAlso?: string;
    children: T[];
  }
>(
  node: T,
  filterWords: string[],
  inheritedMatched: string[] = []
): T | null => {
  if (filterWords.length === 0) return node;

  const newlyMatched = filterWords.filter(
    (word) =>
      inheritedMatched.indexOf(word) < 0 &&
      textMatchesSearchWord(node.title, word)
  );
  const matchedSoFar = inheritedMatched.concat(newlyMatched);
  const remaining = filterWords.filter(
    (word) => matchedSoFar.indexOf(word) < 0
  );

  if (remaining.length === 0) {
    return {
      ...node,
      children: node.children
        .map((child) => filterLineageTree(child, filterWords, matchedSoFar))
        .filter((child): child is T => Boolean(child)),
    };
  }

  const filteredChildren = node.children
    .filter((child) => {
      const blob = collectSubtreeText(child);
      return remaining.every((word) => textMatchesSearchWord(blob, word));
    })
    .map((child) => filterLineageTree(child, filterWords, matchedSoFar))
    .filter((child): child is T => Boolean(child));

  if (filteredChildren.length === 0 && inheritedMatched.length > 0) {
    return null;
  }

  return {
    ...node,
    children: filteredChildren,
  };
};

const collectSubtreeText = (node: {
  title: string;
  nemod?: string;
  see?: string;
  seeAlso?: string;
  children: Array<{
    title: string;
    nemod?: string;
    see?: string;
    seeAlso?: string;
    children: unknown[];
  }>;
}): string =>
  [node.title, node.nemod || "", node.see || "", node.seeAlso || ""]
    .concat(
      node.children.map((child) => collectSubtreeText(child as typeof node))
    )
    .join(" ");

export const seventhCharLookup = (
  codes: string[] | undefined
): Record<string, true> => {
  const lookup: Record<string, true> = {};
  const list = codes ?? [];
  for (let i = 0; i < list.length; i += 1) {
    const key = toDotlessCmCode(list[i]);
    if (key) lookup[key] = true;
  }
  return lookup;
};
